/**
 * TableColumnRow — a sortable row for the table column editor.
 *
 * Each row has: [drag handle] [field picker] [aggregation picker] [delete].
 * Uses @dnd-kit/sortable for drag-and-drop reordering.
 */
import type { CSSProperties } from "react";
import { Button } from "@fluentui/react-components";
import { ChevronDownRegular, DismissRegular, ReOrderDotsVerticalRegular } from "@fluentui/react-icons";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { DatasetViewColumn } from "../../../api/types";
import type { TableColumn, AggregationType } from "../visualConfig";
import { TABLE_AGGREGATIONS, colLabelByName } from "./constants";
import { FieldPickerPopover } from "./fieldPicker";
import OptionPickerPopover from "./OptionPickerPopover";
import { useFieldMappingStyles } from "./styles";

interface TableColumnRowProps {
  id: string;
  col: TableColumn;
  index: number;
  allCols: DatasetViewColumn[];
  styles: ReturnType<typeof useFieldMappingStyles>;
  onChange: (idx: number, col: TableColumn) => void;
  onRemove: (idx: number) => void;
  showDragHandle: boolean;
}

export default function TableColumnRow({
  id,
  col,
  index,
  allCols,
  styles,
  onChange,
  onRemove,
  showDragHandle,
}: TableColumnRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const inlineStyle: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={inlineStyle}
      className={styles.pillRow}
      {...attributes}
    >
      {showDragHandle && (
        <span
          {...listeners}
          style={{ cursor: "grab", display: "flex", alignItems: "center" }}
          aria-label="Drag to reorder"
        >
          <ReOrderDotsVerticalRegular fontSize={16} />
        </span>
      )}

      {/* ── Field picker ── */}
      <FieldPickerPopover
        columns={allCols}
        value={col.column}
        onSelect={(v) => {
          if (!v) return;
          const colMeta = allCols.find((c) => c.name === v);
          const agg: AggregationType =
            colMeta?.semantic_type === "measure" ? "sum" : "none";
          onChange(index, { column: v, aggregation: agg });
        }}
      >
        <Button
          className={styles.dropdown}
          size="small"
          appearance="subtle"
          iconPosition="after"
          icon={<ChevronDownRegular />}
        >
          {col.column ? colLabelByName(col.column, allCols) : "Select field…"}
        </Button>
      </FieldPickerPopover>

      {/* ── Aggregation picker ── */}
      <OptionPickerPopover
        className={styles.aggDropdown}
        options={TABLE_AGGREGATIONS}
        value={col.aggregation}
        onSelect={(v) =>
          onChange(index, { column: col.column, aggregation: v as AggregationType })
        }
      />

      {/* ── Delete button ── */}
      <Button
        appearance="subtle"
        size="small"
        icon={<DismissRegular />}
        onClick={() => onRemove(index)}
        aria-label="Remove column"
        title="Remove column"
      />
    </div>
  );
}